import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Question, OptionKey, DifficultyLevel } from '../../types';
import {
  Plus,
  Search,
  Filter,
  Trash2,
  Edit2,
  CheckCircle2,
  HelpCircle,
  X,
  AlertCircle,
  ArrowRight,
  Check,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';

export const QuestionBankManager: React.FC = () => {
  const {
    questions,
    questionSets,
    subjects,
    addQuestion,
    updateQuestion,
    deleteQuestion,
  } = useApp();
  const { showToast } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterSetId, setFilterSetId] = useState<number | 'all'>('all');
  const [filterDifficulty, setFilterDifficulty] = useState<DifficultyLevel | 'all'>('all');

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [deleteQuestionTarget, setDeleteQuestionTarget] = useState<Question | null>(null);

  const confirmDeleteQuestion = () => {
    if (!deleteQuestionTarget) return;
    deleteQuestion(deleteQuestionTarget.id);
    showToast('Question removed from bank', 'info');
    setDeleteQuestionTarget(null);
  };

  // Question Form State
  const [formData, setFormData] = useState({
    question_set_id: questionSets[0]?.id || 1,
    question_text: '',
    question_text_hi: '',
    option_a: '',
    option_a_hi: '',
    option_b: '',
    option_b_hi: '',
    option_c: '',
    option_c_hi: '',
    option_d: '',
    option_d_hi: '',
    correct_option: 'A' as OptionKey,
    explanation: '',
    explanation_hi: '',
    difficulty: 'Easy' as DifficultyLevel,
    status: 'active' as 'active' | 'inactive',
  });

  // Filter questions
  const filteredQuestions = questions.filter((q) => {
    if (filterSetId !== 'all' && Number(q.question_set_id) !== Number(filterSetId)) return false;
    if (filterDifficulty !== 'all' && q.difficulty !== filterDifficulty) return false;
    if (
      searchQuery &&
      !q.question_text?.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !(q.question_text_hi || '').toLowerCase().includes(searchQuery.toLowerCase()) &&
      !(q.explanation || '').toLowerCase().includes(searchQuery.toLowerCase()) &&
      !(q.explanation_hi || '').toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const handleOpenAdd = () => {
    setEditingQuestion(null);
    setFormData({
      question_set_id: questionSets[0]?.id || 1,
      question_text: '',
      question_text_hi: '',
      option_a: '',
      option_a_hi: '',
      option_b: '',
      option_b_hi: '',
      option_c: '',
      option_c_hi: '',
      option_d: '',
      option_d_hi: '',
      correct_option: 'A',
      explanation: '',
      explanation_hi: '',
      difficulty: 'Easy',
      status: 'active',
    });
    setIsEditModalOpen(true);
  };

  const handleOpenEdit = (q: Question) => {
    setEditingQuestion(q);
    setFormData({
      question_set_id: q.question_set_id,
      question_text: q.question_text,
      question_text_hi: q.question_text_hi || '',
      option_a: q.option_a,
      option_a_hi: q.option_a_hi || '',
      option_b: q.option_b,
      option_b_hi: q.option_b_hi || '',
      option_c: q.option_c,
      option_c_hi: q.option_c_hi || '',
      option_d: q.option_d,
      option_d_hi: q.option_d_hi || '',
      correct_option: q.correct_option,
      explanation: q.explanation || '',
      explanation_hi: q.explanation_hi || '',
      difficulty: q.difficulty,
      status: q.status,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveQuestion = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.question_text.trim()) {
      showToast('English question statement cannot be empty', 'error');
      return;
    }

    if (editingQuestion) {
      updateQuestion(editingQuestion.id, formData);
      showToast('Question updated successfully', 'success');
    } else {
      addQuestion(formData);
      showToast('New question created in bank', 'success');
    }
    setIsEditModalOpen(false);
  };

  return (
    <div className="space-y-6 pb-24 md:pb-16">
      {/* 1. Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Question Bank</h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Create, edit, and organize multiple-choice questions with step-by-step solutions.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            id="add-question-btn"
            onClick={handleOpenAdd}
            className="px-4 py-2 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4 text-[#111111]" />
            New Question
          </button>
        </div>
      </div>

      {/* 2. Search & Filters Strip */}
      <div className="bg-white p-4 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)] flex flex-col md:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-[#5F6368] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search questions by keyword, topic, or solution text..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] focus:ring-1 focus:ring-[#56E8E1] bg-[#F7FAFA]"
          />
        </div>

        <div className="w-full md:w-60">
          <select
            value={filterSetId}
            onChange={(e) =>
              setFilterSetId(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
          >
            <option value="all">All Question Sets</option>
            {questionSets.map((qs) => (
              <option key={qs.id} value={qs.id}>
                {qs.name}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full md:w-40">
          <select
            value={filterDifficulty}
            onChange={(e) =>
              setFilterDifficulty(e.target.value as DifficultyLevel | 'all')
            }
            className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
          >
            <option value="all">All Difficulties</option>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>
        </div>
      </div>

      <div className="text-xs text-[#5F6368] font-semibold px-1">
        Showing {filteredQuestions.length} of {questions.length} questions
      </div>

      {/* 3. Questions List */}
      <div className="space-y-4">
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-[#E5E9E9] p-8 shadow-xs">
            <HelpCircle className="w-12 h-12 text-[#5F6368]/40 mx-auto mb-3" />
            <h3 className="text-base font-bold text-[#111111] mb-1">No Questions Found</h3>
            <p className="text-[#5F6368] text-xs mb-4">
              Try adjusting your search query or filter options, or create a new question.
            </p>
            <button
              onClick={handleOpenAdd}
              className="px-4 py-2 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] rounded-xl text-xs font-bold transition-all cursor-pointer"
            >
              Add First Question
            </button>
          </div>
        ) : (
          filteredQuestions.map((q, idx) => {
            const parentSet = questionSets.find((s) => s.id === q.question_set_id);
            const parentSubject = parentSet
              ? subjects.find((sub) => sub.id === parentSet.subject_id)
              : null;

            return (
              <div
                key={q.id}
                className="bg-white rounded-2xl border border-[#E5E9E9] p-5 sm:p-6 space-y-4 shadow-[0_2px_12px_rgba(17,17,17,0.02)] hover:border-[#56E8E1]/60 transition-colors"
              >
                {/* Header Strip */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="w-6 h-6 rounded-lg bg-[#E8FAF9] text-[#1b8e88] font-extrabold text-xs flex items-center justify-center border border-[#56E8E1]/30">
                      {idx + 1}
                    </span>
                    {parentSet && (
                      <span className="px-2.5 py-0.5 rounded-md bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40 text-[11px] font-semibold">
                        {parentSet.name}
                      </span>
                    )}
                    {parentSubject && (
                      <span className="text-[11px] text-[#5F6368] font-medium">
                        • {parentSubject.name}
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                        q.difficulty === 'Easy'
                          ? 'bg-[#EAF8F1] text-[#20A66A] border border-[#20A66A]/30'
                          : q.difficulty === 'Medium'
                          ? 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40'
                          : 'bg-[#FDEDEF] text-[#D94A5C] border border-[#D94A5C]/30'
                      }`}
                    >
                      {q.difficulty}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEdit(q)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#1b8e88] hover:bg-[#E8FAF9] transition-colors cursor-pointer"
                      title="Edit question"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteQuestionTarget(q)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] transition-colors cursor-pointer"
                      title="Delete question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Question Statement */}
                <p className="text-sm sm:text-base font-bold text-[#111111] leading-relaxed">
                  {q.question_text}
                </p>

                {/* 4 Options Preview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {(['A', 'B', 'C', 'D'] as const).map((optKey) => {
                    const text =
                      optKey === 'A'
                        ? q.option_a
                        : optKey === 'B'
                        ? q.option_b
                        : optKey === 'C'
                        ? q.option_c
                        : q.option_d;
                    const isCorrect = q.correct_option === optKey;

                    return (
                      <div
                        key={optKey}
                        className={`p-3 rounded-xl border flex items-start gap-2.5 transition-colors ${
                          isCorrect
                            ? 'border-[#20A66A]/40 bg-[#EAF8F1] text-[#111111] font-semibold'
                            : 'border-[#E5E9E9] bg-[#F7FAFA] text-[#111111]'
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-lg text-[10px] font-bold flex items-center justify-center shrink-0 border ${
                            isCorrect
                              ? 'bg-[#20A66A] text-white border-[#20A66A]'
                              : 'bg-white text-[#111111] border-[#E5E9E9]'
                          }`}
                        >
                          {optKey}
                        </span>
                        <span className="flex-1 leading-snug">{text}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Conceptual Explanation */}
                {q.explanation && (
                  <div className="p-3.5 rounded-xl bg-[#F7FAFA] border border-[#E5E9E9] text-xs text-[#111111] space-y-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#1b8e88] flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-[#1fb4ac]" />
                      Explanation
                    </div>
                    <p className="leading-relaxed whitespace-pre-line text-[#111111]">
                      {q.explanation}
                    </p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add / Edit Question Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9] my-8">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-5">
              <h3 className="text-base font-bold text-[#111111]">
                {editingQuestion ? 'Edit MCQ Question' : 'Create New MCQ Question'}
              </h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveQuestion} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Target Question Set
                  </label>
                  <select
                    value={formData.question_set_id}
                    onChange={(e) =>
                      setFormData({ ...formData, question_set_id: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    {questionSets.map((qs) => (
                      <option key={qs.id} value={qs.id}>
                        {qs.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Difficulty Level
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) =>
                      setFormData({ ...formData, difficulty: e.target.value as DifficultyLevel })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Question Text / Statement (English)
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Enter the complete English MCQ question statement..."
                  value={formData.question_text}
                  onChange={(e) =>
                    setFormData({ ...formData, question_text: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#1b8e88] mb-1">
                  Question Text / Statement (हिन्दी - Optional)
                </label>
                <textarea
                  rows={3}
                  placeholder="हिंदी में प्रश्न दर्ज करें (Optional)..."
                  value={formData.question_text_hi}
                  onChange={(e) =>
                    setFormData({ ...formData, question_text_hi: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-[#F7FAFA]"
                />
              </div>

              {/* 4 Options */}
              <div className="space-y-3">
                <label className="block text-xs font-bold text-[#111111]">
                  Multiple Choice Options & Designate Correct Answer
                </label>
                {(['A', 'B', 'C', 'D'] as const).map((opt) => (
                  <div key={opt} className="space-y-1.5 p-3 rounded-2xl border border-[#E5E9E9] bg-[#F7FAFA]">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                        <input
                          type="radio"
                          name="correct_option"
                          value={opt}
                          checked={formData.correct_option === opt}
                          onChange={() => setFormData({ ...formData, correct_option: opt })}
                          className="accent-[#56E8E1] focus:ring-[#56E8E1]"
                        />
                        <span
                          className={`w-7 h-7 rounded-xl text-xs font-bold flex items-center justify-center border transition-all ${
                            formData.correct_option === opt
                              ? 'bg-[#20A66A] text-white border-[#20A66A] shadow-2xs'
                              : 'bg-white text-[#111111] border-[#E5E9E9]'
                          }`}
                        >
                          {opt}
                        </span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder={`Option ${opt} (English)`}
                        value={
                          opt === 'A'
                            ? formData.option_a
                            : opt === 'B'
                            ? formData.option_b
                            : opt === 'C'
                            ? formData.option_c
                            : formData.option_d
                        }
                        onChange={(e) => {
                          const key =
                            opt === 'A'
                              ? 'option_a'
                              : opt === 'B'
                              ? 'option_b'
                              : opt === 'C'
                              ? 'option_c'
                              : 'option_d';
                          setFormData({ ...formData, [key]: e.target.value });
                        }}
                        className="flex-1 px-3.5 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                      />
                    </div>
                    <div className="pl-11">
                      <input
                        type="text"
                        placeholder={`Option ${opt} (हिन्दी - Optional)`}
                        value={
                          opt === 'A'
                            ? formData.option_a_hi
                            : opt === 'B'
                            ? formData.option_b_hi
                            : opt === 'C'
                            ? formData.option_c_hi
                            : formData.option_d_hi
                        }
                        onChange={(e) => {
                          const key =
                            opt === 'A'
                              ? 'option_a_hi'
                              : opt === 'B'
                              ? 'option_b_hi'
                              : opt === 'C'
                              ? 'option_c_hi'
                              : 'option_d_hi';
                          setFormData({ ...formData, [key]: e.target.value });
                        }}
                        className="w-full px-3.5 py-1.5 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Explanation (English)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="English explanation..."
                    value={formData.explanation}
                    onChange={(e) =>
                      setFormData({ ...formData, explanation: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#1b8e88] mb-1">
                    Explanation (हिन्दी - Optional)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="हिंदी व्याख्या..."
                    value={formData.explanation_hi}
                    onChange={(e) =>
                      setFormData({ ...formData, explanation_hi: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-[#F7FAFA]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#E5E9E9]">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {editingQuestion ? 'Save Changes' : 'Create Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteQuestionTarget}
        title="Delete Question"
        message="Delete this question permanently from the bank?"
        confirmText="Delete Question"
        variant="danger"
        onConfirm={confirmDeleteQuestion}
        onCancel={() => setDeleteQuestionTarget(null)}
      />
    </div>
  );
};
